<!--
Tiptap can render this widget before EditorContent forwards Vue's injection context.
Keep routing dependencies in ScratchPad.vue: pass data/callbacks through widget props instead of using useRoute, useRouter or injected case params here.
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, toRaw, useTemplateRef, watch } from "vue";
import VoiceTranscriptUtterances from "@/components/CasePanels/RecordView/VoiceTranscript/VoiceTranscriptUtterances.vue";
import { getLabelTint } from "@recordsure/unity-scratchpad/document";
import { copyScratchpadSource, type ScratchpadBlockMetadataProps } from "@recordsure/unity-scratchpad/extensions";

const props = defineProps<ScratchpadBlockMetadataProps>();

const sourceContent = useTemplateRef("source-content");

const copyState = ref<"copied" | "failed">();

const labelInput = useTemplateRef("label-input");

// The getter reads the parent's reactive state, so this re-runs when a new source is captured.
watch(
  () =>
    !props.readonly &&
    props.source != null &&
    toRaw(props.source) === toRaw(props.latestAddedDataPointSource?.() ?? null),
  (focus) => focus && void nextTick(() => labelInput.value?.focus()),
);

const editingFrom = ref<string>();

const shownLabel = computed(() => editingFrom.value ?? props.datapointLabel);

const badgeStyle = computed(() => {
  if (shownLabel.value === "") return;

  const tint = getLabelTint(shownLabel.value);
  if (tint == null) return;

  return { background: tint.background, color: tint.foreground };
});

const details = computed(
  () => `Edited ${props.updatedAt != null ? new Date(props.updatedAt).toLocaleString() : "just now"}`,
);

function moveToValue(event: KeyboardEvent) {
  if (event.isComposing) return;
  if (props.focusContent()) event.preventDefault();
}

let resetCopyState: ReturnType<typeof setTimeout> | undefined;
onBeforeUnmount(() => clearTimeout(resetCopyState));
async function copySource() {
  clearTimeout(resetCopyState);
  copyState.value = undefined;
  try {
    await copyScratchpadSource(sourceContent.value!);
    copyState.value = "copied";
  } catch (cause) {
    console.error("Unable to copy scratchpad source", cause);
    copyState.value = "failed";
  }
  resetCopyState = setTimeout(() => (copyState.value = undefined), 2000);
}
</script>
<template>
  <div class="scratchpad-block-chrome" :class="{ 'scratchpad-block-active': isActive }" contenteditable="false">
    <div
      class="scratchpad-block-metadata"
      :class="{ 'scratchpad-block-labelled': isDatapoint }"
      contenteditable="false"
    >
      <template v-if="isDatapoint">
        <span
          v-if="readonly"
          tabindex="0"
          class="scratchpad-point-badge"
          :class="{ 'scratchpad-point-unlabelled': badgeStyle == null }"
          :style="badgeStyle"
          :title="datapointLabel === '' ? undefined : datapointLabel"
        >
          {{ datapointLabel === "" ? "Unlabelled" : datapointLabel }}
        </span>
        <input
          v-else
          ref="label-input"
          :key="shownLabel"
          type="text"
          aria-label="Data point label"
          placeholder="Add Label"
          class="scratchpad-point-badge"
          :class="{ 'scratchpad-point-unlabelled': badgeStyle == null }"
          :defaultValue="datapointLabel"
          :style="badgeStyle"
          @keydown.enter.exact="moveToValue"
          @keydown.tab.exact="moveToValue"
          @focus="editingFrom = datapointLabel"
          @blur="editingFrom = undefined"
          @input="(event) => updateDatapointLabel((event.target as HTMLInputElement).value.trim())"
        />
      </template>
      <span class="scratchpad-block-details">{{ details }}</span>
    </div>
    <blockquote v-if="source != null" class="scratchpad-source">
      <button
        v-if="onRedirectTo != null"
        type="button"
        class="tw-text-blue-700 tw-underline hover:tw-text-blue-900 focus-visible:tw-outline focus-visible:tw-outline-2"
        @click="onRedirectTo(source)"
      >
        Go to source
      </button>
      <div ref="source-content">
        <VoiceTranscriptUtterances
          v-if="source.kind === 'transcript'"
          :file-reference="source.fileReference"
          :list="source.utteranceList"
        />
        <p v-else class="scratchpad-source-text">{{ source.text }}</p>
      </div>
      <button
        type="button"
        class="scratchpad-source-copy"
        aria-label="Copy source text"
        title="Copy source text"
        :data-copy-state="copyState"
        @mousedown.prevent
        @click="copySource"
      >
        <i
          class="fa"
          :class="copyState === 'copied' ? 'fa-check' : copyState === 'failed' ? 'fa-triangle-exclamation' : 'fa-copy'"
          aria-hidden="true"
        ></i>
      </button>
    </blockquote>
    <span v-if="source != null" role="status" class="scratchpad-visually-hidden">
      {{
        copyState === "copied" ? "Source text copied" : copyState === "failed" ? "Source text couldn't be copied" : ""
      }}
    </span>
  </div>
</template>
